import { useCallback, useMemo } from 'react';
import Unit from '../../types/Unit/Unit';
import { getAllCombosOfDamage, getDiceDetails, getProbabilityDistribution, getWardDistribution, getWoundProbability } from './useAttackEvaluation.util';
import { attackConversionValues, saveValues } from '../../Probability/ProbabilityHome.constant';
import Attack from '../../types/Attack/Attack';

interface UseAttackEvaluationProps {
  unit: Unit;
  attackBonus: number;
  hitBonus: number;
  woundBonus: number;
  rendBonus: number;
  damageBonus: number;
  attackBonusEC: boolean;
  hitBonusEC: boolean;
  woundBonusEC: boolean;
  rendBonusEC: boolean;
  damageBonusEC: boolean;
  save: string;
  ward: string;
  isEthereal: boolean;
  didCharge: boolean;
  rangeType?: string;
}

export interface AttackProfile {
  name: string;
  numAttacks: number | string;
  hit: number;
  wound: number;
  rend: number;
  damage: number | string;
  critType: string;
}

function useAttackEvaluation({
  unit,
  attackBonus,
  hitBonus,
  woundBonus,
  rendBonus,
  damageBonus,
  attackBonusEC,
  hitBonusEC,
  woundBonusEC,
  rendBonusEC,
  damageBonusEC,
  save,
  ward,
  isEthereal,
  didCharge,
  rangeType = 'MELEE'
}: UseAttackEvaluationProps) {
  const getAttackProfile = useCallback((attack: Attack): AttackProfile => {
    const hitBase = attackConversionValues.get(attack.hitType) ?? 7;
    const woundBase = attackConversionValues.get(attack.woundType) ?? 7;
    const chargeBonus = attack.hasChargeDamage && didCharge ? 1 : 0;
    const championBonus = attack.hasChampion ? 1 : 0;

    let numAttacks: string | number;
    const expectedAttackBonus = (attackBonus < 0 || attack.isCompanion && attackBonusEC || !attack.isCompanion ? attackBonus : 0);

    if(attack.numModels === 0) {
      numAttacks = 0;
    } else if(attack.numAttacks.includes('D')) {
      if(expectedAttackBonus !== 0) {
        const { numDice, diceSize, modifier } = getDiceDetails(attack.numAttacks);
        numAttacks = `${numDice * attack.numModels}D${diceSize}+${(expectedAttackBonus + modifier) * attack.numModels + championBonus}`;
      } else {
        numAttacks = attack.numAttacks;
      }
    } else {
      numAttacks = Math.max((Number(attack.numAttacks) + expectedAttackBonus), 1) * attack.numModels + championBonus;
    }

    let damage: string | number;
    const expectedDamageBonus = chargeBonus +
      (damageBonus < 0 || attack.isCompanion && damageBonusEC || !attack.isCompanion ? damageBonus : 0);

    if(attack.damage.includes('D')) {
      if(expectedDamageBonus !== 0) {
        const { numDice, diceSize, modifier} = getDiceDetails(attack.damage);

        damage = `${numDice}D${diceSize}+${expectedDamageBonus + modifier}`;
      } else {
        damage = attack.damage;
      }
    } else {
      damage = Math.max(Number(attack.damage) + expectedDamageBonus, 1);
    }

    return {
      name: attack.name,
      numAttacks,
      hit: hitBase -
        (hitBonus < 0 || attack.isCompanion && hitBonusEC || !attack.isCompanion ? hitBonus : 0),
      wound: woundBase -
        (woundBonus < 0 || attack.isCompanion && woundBonusEC || !attack.isCompanion ? woundBonus : 0),
      rend: attack.rend +
        (rendBonus < 0 || attack.isCompanion && rendBonusEC || !attack.isCompanion ? rendBonus : 0),
      damage,
      critType: attack.critType
    };
  }, [
    attackBonus,
    hitBonus,
    woundBonus,
    rendBonus,
    damageBonus,
    attackBonusEC,
    hitBonusEC,
    woundBonusEC,
    rendBonusEC,
    damageBonusEC,
    didCharge
  ]);

  // const test = combineDependentSumDist([0.5723, 0.3455, 0.0670, 0.0052], 3);
  // console.log(test);
  // console.log(test.reduce((prev, curr) => prev + curr, 0));

  const getDamageDistribution = useCallback((profile: AttackProfile) => {
    let maxDamage: number;
    let damage: number;

    let numAttacks: number;

    if(typeof profile.numAttacks === 'string') {
      const { numDice, diceSize, modifier} = getDiceDetails(profile.numAttacks);
      numAttacks = Math.floor((diceSize + 1) * numDice / 2 + modifier);
    } else {
      numAttacks = Number(profile.numAttacks);
    }

    if(typeof profile.damage === 'string') {
      const { numDice, diceSize, modifier} = getDiceDetails(profile.damage);

      maxDamage = numAttacks * (diceSize * numDice + modifier);
      damage = (diceSize + 1) / 2 * numDice + modifier;
    } else {
      maxDamage = numAttacks * Number(profile.damage);
      damage = Number(profile.damage);
    }


    if(maxDamage > 100) {
      return [];
    } else {
      const saveValue = saveValues.get(save) ?? 7;
      const wardValue = saveValues.get(ward) ?? 7;

      const woundProbability = getWoundProbability(
        profile.hit,
        profile.wound,
        profile.rend,
        profile.critType,
        saveValue,
        isEthereal
      );

      const woundDist = getProbabilityDistribution(numAttacks, woundProbability);

      return getWardDistribution(woundDist, damage, wardValue);
    }
  },[
    save, 
    ward,
    isEthereal
  ]);

  const getCombinedDamageDistribution = useCallback((attackDists: number[][]) => {
    let prev: number[] = [];
    attackDists.forEach((dist) => {
      const numberOfZeroes = prev.length === 0 ? 0 : 1 + dist.length === 0 ? 0 : 1;
      const next: number[] = Array(dist.length + prev.length - numberOfZeroes).fill(0);
      for(let i = 0; i < next.length; i += 1) {
        const combinations = getAllCombosOfDamage(i, prev.length - 1, dist.length - 1);
        if(combinations.length === 0) {
          next[i] = prev[i] ? prev[i] : (dist[i] ? dist[i] : 0);
        } else {
          combinations.forEach((combo) => {
            next[i] += prev[combo[0]] * dist[combo[1]];
          });
        }
      }

      prev = next;
    });

    return prev;
  }, []);

  const attackDistributions = useMemo(() => {
    const output: number[][] = [];

    unit.attacks.forEach((attack) => {
      if(attack.rangeType === rangeType && attack.numAttacks !== '0') {
        const profile = getAttackProfile(attack);
        output.push(getDamageDistribution(profile));
      }
    });

    return output;
  }, [unit, getAttackProfile, getDamageDistribution, rangeType]);


  const damageDistribution = useMemo(
    () => getCombinedDamageDistribution(attackDistributions), 
    [getCombinedDamageDistribution, attackDistributions]
  );

  const averageDamage = useMemo(() => {
    let output = 0;
    damageDistribution.forEach((val, i) => {
      output += val * i;
    });
    return output;
  }, [damageDistribution]);

  const attackProfiles = useMemo(() => unit.attacks.filter((a) => a.rangeType === rangeType).map((a) => getAttackProfile(a)), [unit, getAttackProfile, rangeType]);

  return {
    damageDistribution,
    averageDamage,
    attackProfiles
  };
}

export default useAttackEvaluation;